/**
 * @file OrderPaymentCard.tsx
 * @description Карточка платежа во вкладке «Оплаты» заказа (вынесена из
 * `OrderPaymentsTab`, phase19.31). Вкладку перестраивает phase20 — здесь
 * минимум по SC50: статус пилюлей тона (SC26), способ, номер обычным шрифтом,
 * сумма «₽», время по поясу магазина.
 */

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { StatusPill } from "../ui/StatusPill";
import {
  currencySymbol,
  formatMoney,
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUS_LABEL,
  type PaymentDto,
  type PaymentStatus,
} from "../../lib/api-orders";
import type { StatusTone } from "../../lib/shop/status-meta";
import { formatShopDateTime } from "../../lib/shop-time";

/** Тон статуса платежа: получен — зелёный, ждём — янтарный, сбой и возврат — красный. */
const PAYMENT_STATUS_TONE: Record<PaymentStatus, StatusTone> = {
  COMPLETED: "success",
  PENDING: "attention",
  FAILED: "error",
  REFUNDED: "error",
};

interface Props {
  payment: PaymentDto;
  /** Атрибуты строки для подсветки по `?focus=`. */
  rowAttr: { "data-row-id": string };
  highlighted: boolean;
  /** Кнопка «Вернуть» — только с правом и у полученного платежа. */
  onRefund?: () => void;
}

export function OrderPaymentCard({ payment: p, rowAttr, highlighted, onRefund }: Props) {
  return (
    <EditorialCard {...rowAttr} className={highlighted ? "ring-2 ring-primary ring-offset-2" : undefined}>
      <EditorialCard.Body className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={PAYMENT_STATUS_TONE[p.status]}>{PAYMENT_STATUS_LABEL[p.status]}</StatusPill>
            <Badge variant="secondary">{PAYMENT_METHOD_LABEL[p.method]}</Badge>
            {/* Номер платежа (phase19.24 §12 п.1) — по нему платёж
                узнают в «Пути заказа» и в переписке с бухгалтерией. */}
            {p.number ? (
              <span className="type-body font-medium tabular-nums text-on-surface-variant">{p.number}</span>
            ) : null}
          </div>
          <div className="type-body mt-1 font-semibold tabular-nums text-on-surface">
            {formatMoney(p.amount)} {currencySymbol(p.currency)}
          </div>
          <p className="type-caption mt-1 text-on-surface-variant">
            {p.paidAt ? `Оплачен ${formatShopDateTime(p.paidAt)}` : "В обработке"}
            {p.refundedAt ? ` · возврат ${formatShopDateTime(p.refundedAt)}` : ""}
          </p>
          {p.refundReason ? (
            <p className="type-caption mt-1 text-on-surface-variant">Причина возврата: {p.refundReason}</p>
          ) : null}
        </div>
        {onRefund ? (
          <Button variant="outline" onClick={onRefund}>
            Вернуть
          </Button>
        ) : null}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
